import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { Theme, ThemeColors, THEMES_PREDEFINIS, THEME_DEFAUT } from './themes';

interface ThemeContextValue {
  theme: Theme;
  setTheme: (slug: string, customColors?: Partial<ThemeColors>) => void;
  themesDisponibles: Theme[];
}

const ThemeContext = createContext<ThemeContextValue>({
  theme: THEME_DEFAUT,
  setTheme: () => {},
  themesDisponibles: THEMES_PREDEFINIS,
});

export const useTheme = () => useContext(ThemeContext);

function applyTheme(couleurs: ThemeColors) {
  const root = document.documentElement;
  root.style.setProperty('--color-primary',          couleurs.primary);
  root.style.setProperty('--color-primary-hover',    couleurs.primaryHover);
  root.style.setProperty('--color-primary-light',    couleurs.primaryLight);
  root.style.setProperty('--color-primary-dark',     couleurs.primaryDark);
  root.style.setProperty('--color-primary-text',     couleurs.primaryText);
  root.style.setProperty('--color-primary-muted',    couleurs.primaryMuted);
  root.style.setProperty('--color-accent',           couleurs.accent);
  root.style.setProperty('--color-accent-light',     couleurs.accentLight);
  root.style.setProperty('--color-surface',          couleurs.surface);
  root.style.setProperty('--color-surface-raised',   couleurs.surfaceRaised);
  root.style.setProperty('--color-surface-overlay',  couleurs.surfaceOverlay);
  root.style.setProperty('--color-content',          couleurs.content);
  root.style.setProperty('--color-content-muted',    couleurs.contentMuted);
  root.style.setProperty('--color-content-subtle',   couleurs.contentSubtle);
  root.style.setProperty('--color-content-inverted', couleurs.contentInverted);
  root.style.setProperty('--color-border',           couleurs.border);
  root.style.setProperty('--color-border-strong',    couleurs.borderStrong);
  root.style.setProperty('--color-success',          couleurs.success);
  root.style.setProperty('--color-success-light',    couleurs.successLight);
  root.style.setProperty('--color-warning',          couleurs.warning);
  root.style.setProperty('--color-warning-light',    couleurs.warningLight);
  root.style.setProperty('--color-danger',           couleurs.danger);
  root.style.setProperty('--color-danger-light',     couleurs.dangerLight);
  root.style.setProperty('--color-sidebar-bg',       couleurs.sidebarBg);
  root.style.setProperty('--color-sidebar-text',     couleurs.sidebarText);
  root.style.setProperty('--color-sidebar-active',   couleurs.sidebarActive);
  root.style.setProperty('--color-sidebar-hover',    couleurs.sidebarHover);
  root.style.setProperty('--color-sidebar-border',   couleurs.sidebarBorder);
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<Theme>(THEME_DEFAUT);

  useEffect(() => {
    // Charger depuis localStorage (instantané), API backend en arrière-plan
    const savedSlug   = localStorage.getItem('payslip-theme-slug');
    const savedCustom = localStorage.getItem('payslip-theme-custom');
    const found = THEMES_PREDEFINIS.find(t => t.slug === savedSlug) ?? THEME_DEFAUT;
    let couleurs: ThemeColors = { ...found.couleurs };
    if (savedCustom) {
      try { couleurs = { ...couleurs, ...JSON.parse(savedCustom) }; } catch {}
    }
    setThemeState({ ...found, couleurs });
    applyTheme(couleurs);
  }, []);

  const setTheme = (slug: string, customColors?: Partial<ThemeColors>) => {
    const found = THEMES_PREDEFINIS.find(t => t.slug === slug) ?? THEME_DEFAUT;
    const couleurs: ThemeColors = { ...found.couleurs, ...customColors };
    setThemeState({ ...found, couleurs });
    applyTheme(couleurs);
    localStorage.setItem('payslip-theme-slug', slug);
    if (customColors && Object.keys(customColors).length > 0) {
      localStorage.setItem('payslip-theme-custom', JSON.stringify(customColors));
    } else {
      localStorage.removeItem('payslip-theme-custom');
    }
  };

  return (
    <ThemeContext.Provider value={{ theme, setTheme, themesDisponibles: THEMES_PREDEFINIS }}>
      {children}
    </ThemeContext.Provider>
  );
}
